import React, { useCallback, useState, useRef } from "react";
import { TextFieldAtom } from "../atom";

/**
 * DebouncedInput - A reusable input component with optional debouncing
 *
 * @param {string|number} value - Current value
 * @param {Function} onChange - Change handler (position, normalizedValue) - triggered after debounce delay during typing
 * @param {Function} onBlur - Blur handler (position, normalizedValue) - triggered immediately when input loses focus
 * @param {Object} position - Position/entity object to pass to onChange/onBlur
 * @param {number} debounceDelay - Delay in ms before triggering onChange (default: 300)
 * @param {number} decimalPlaces - Number of decimal places for numeric inputs (default: 3)
 * @param {boolean} normalizeDecimals - Whether to normalize comma to dot (default: true)
 * @param {string} className - CSS class name
 * @param {Object} props - Additional props passed to TextFieldAtom
 */
const DebouncedInput = React.memo(
    ({
        value,
        onChange,
        onBlur,
        position,
        debounceDelay = 300,
        decimalPlaces = 3,
        normalizeDecimals = true,
        className,
        ...props
    }) => {
        const [localValue, setLocalValue] = useState(value === 0 ? "" : value);
        const timeoutRef = useRef(null);
        const isInternalUpdateRef = useRef(false);

        // Update local value when external value changes
        // But preserve the format if it's just been updated internally
        React.useEffect(() => {
            if (!isInternalUpdateRef.current) {
                setLocalValue(value === 0 ? "" : value);
            }
            isInternalUpdateRef.current = false;
        }, [value]);

        const normalizeValue = useCallback(
            (val) => {
                if (!normalizeDecimals) return val;
                // Normalize: replace comma with dot for consistent decimal separator
                return val.toString().replace(/,/g, ".");
            },
            [normalizeDecimals],
        );

        const handleChange = useCallback(
            (e) => {
                if (!e || !e.target) return;

                const newValue = e.target.value;
                setLocalValue(newValue);

                // Only trigger debounced onChange if onChange handler is provided
                if (!onChange) return;

                // Clear existing timeout
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }

                // Set new timeout for debounced update
                timeoutRef.current = setTimeout(() => {
                    isInternalUpdateRef.current = true;
                    const normalizedValue = normalizeValue(newValue);
                    onChange(position, normalizedValue);
                }, debounceDelay);
            },
            [onChange, position, debounceDelay, normalizeValue],
        );

        const handleBlur = useCallback(
            (e) => {
                // Clear any pending debounced onChange
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }

                const currentValue = localValue !== undefined ? localValue : "";

                // If custom onBlur handler is provided, use it (for blur-only saves)
                if (onBlur && currentValue !== "" && position) {
                    isInternalUpdateRef.current = true;
                    const normalizedValue = normalizeValue(currentValue);
                    onBlur(position, normalizedValue);
                }
                // Otherwise, trigger onChange immediately on blur if onChange is provided
                else if (onChange && currentValue !== "" && position) {
                    isInternalUpdateRef.current = true;
                    const normalizedValue = normalizeValue(currentValue);
                    onChange(position, normalizedValue);
                }

                // Call the original onBlur from props if provided (for other blur behaviors like onWheel)
                if (props.onBlur) {
                    props.onBlur(e);
                }
            },
            [onChange, onBlur, position, localValue, normalizeValue, props],
        );

        // Cleanup timeout on unmount
        React.useEffect(() => {
            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }, []);

        return (
            <TextFieldAtom
                {...props}
                className={className}
                value={localValue}
                onChange={handleChange}
                onBlur={handleBlur}
                decimalPlaces={decimalPlaces}
            />
        );
    },
);

DebouncedInput.displayName = "DebouncedInput";

export default DebouncedInput;
